import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { NewInquiryDialog } from "@/components/forms/new-inquiry-dialog";
import { inquiries, fmtUsd, type InquiryStatus } from "@/mocks/data";

export const Route = createFileRoute("/leads")({
  head: () => ({
    meta: [
      { title: "Inquiries — Studio OS" },
      { name: "description", content: "Incoming photography inquiries and lead pipeline." },
    ],
  }),
  component: LeadsPage,
});

const columns: { key: InquiryStatus; label: string }[] = [
  { key: "new", label: "New" },
  { key: "pending", label: "Quoted" },
  { key: "booked", label: "Booked" },
  { key: "archived", label: "Archived" },
];

function LeadsPage() {
  return (
    <div className="mx-auto max-w-7xl px-6 py-8 lg:px-10 lg:py-10">
      <PageHeader
        title="Inquiries"
        description="Track every lead from first contact through to booked shoot."
        actions={<NewInquiryDialog />}
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {columns.map((col) => {
          const items = inquiries.filter((i) => i.status === col.key);
          const total = items.reduce((sum, i) => sum + i.budget, 0);
          return (
            <div key={col.key} className="rounded-xl bg-card ring-1 ring-border">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                  <h2 className="text-xs font-semibold uppercase tracking-widest">{col.label}</h2>
                  <span className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                    {items.length}
                  </span>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  {fmtUsd(total, { compact: true })}
                </span>
              </div>
              <div className="space-y-2 p-3">
                {items.length === 0 && (
                  <p className="px-2 py-6 text-center text-xs text-muted-foreground">No inquiries.</p>
                )}
                {items.map((i) => (
                  <div key={i.id} className="rounded-lg bg-surface-muted/60 p-3 ring-1 ring-border">
                    <div className="mb-1 flex items-start justify-between gap-2">
                      <p className="text-sm font-medium">{i.client}</p>
                      <span className="font-mono text-[10px] text-muted-foreground">{i.date}</span>
                    </div>
                    <p className="mb-2 text-xs text-muted-foreground">{i.category}</p>
                    {i.notes ? <p className="mb-2 line-clamp-2 text-xs italic text-muted-foreground">{i.notes}</p> : null}
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs font-medium">{fmtUsd(i.budget, { compact: true })}</span>
                      <StatusBadge status={i.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
